import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import {
  STAKEHOLDER_ROLE_TYPES,
  deactivateStakeholder,
  employeeLabelOf,
  listEmployees,
  listStakeholdersByTerritory,
  listTerritories,
  upsertStakeholder,
  type EmployeeRow,
  type StakeholderRoleType,
  type StakeholderRow,
  type TerritoryRow,
} from '../../api/orgApi.js';
import { CheckboxField, Field, SelectField, sx, toOptions, todayIso } from './orgKit.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

const FAR_FUTURE = '9999-12-31';

export function StakeholderPage(): ReactNode {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [territories, setTerritories] = useState<TerritoryRow[]>([]);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [territoryId, setTerritoryId] = useState('');
  const [rows, setRows] = useState<StakeholderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [roleType, setRoleType] = useState<StakeholderRoleType>('SALES_OFFICER');
  const [employeeId, setEmployeeId] = useState('');
  const [isPrimary, setIsPrimary] = useState(true);
  const [isBackup, setIsBackup] = useState(false);
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listTerritories(token).then((r) => setTerritories(r.data)).catch((e) => setError((e as Error).message));
    listEmployees(token).then((r) => setEmployees(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  const empName = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.userId ?? e.empExtId, employeeLabelOf(e)])),
    [employees],
  );

  useEffect(() => {
    if (!territoryId) { setRows([]); return; }
    setLoading(true);
    listStakeholdersByTerritory(token, territoryId)
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [territoryId, token]);

  function reload(): void {
    if (!territoryId) return;
    listStakeholdersByTerritory(token, territoryId).then((r) => setRows(r.data)).catch((e) => setError((e as Error).message));
  }

  async function add(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!territoryId) { setError(t('org.stakeholder.selectFirst')); return; }
    if (!employeeId) { setError(t('org.stakeholder.selectEmployee')); return; }
    if (isPrimary && isBackup) { setError(t('org.stakeholder.bothError')); return; }
    setError(null);
    setBusy(true);
    try {
      await upsertStakeholder(token, { territoryId, roleType, employeeId, isPrimary, isBackup, effectiveFromDate });
      setNotice(t('org.stakeholder.saved'));
      setShowAdd(false);
      setEmployeeId('');
      reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(sid: string): Promise<void> {
    if (!window.confirm(t('org.stakeholder.confirmDeactivate'))) return;
    setError(null);
    try {
      await deactivateStakeholder(token, sid);
      setNotice(t('org.stakeholder.deactivated'));
      reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const territoryOptions = territories.map((t) => ({ value: t.territoryId, label: `${t.territoryCode} — ${t.territoryName}` }));
  // t040.employee_id is a foreign key onto the IAM user, so an empExtId here
  // is rejected as a missing reference.
  const employeeOptions = employees
    .filter((e) => !!e.userId)
    .map((e) => ({ value: e.userId as string, label: employeeLabelOf(e) }));

  return (
    <main aria-label="stakeholder-list" style={sx.main}>
      <div style={sx.headerRow}>
        <h1>{t('org.stakeholder.title')}</h1>
        {territoryId && (
          <Button type="button" variant="primary" onClick={() => { setShowAdd((v) => !v); setNotice(null); }}>
            {showAdd ? t('org.common.close') : t('org.stakeholder.assign')}
          </Button>
        )}
      </div>

      <div style={{ maxWidth: 460, marginBottom: 16 }}>
        <SelectField
          label={t('org.field.territory')}
          name="territoryId"
          value={territoryId}
          onChange={(v) => { setTerritoryId(v); setShowAdd(false); setNotice(null); setError(null); }}
          options={territoryOptions}
          placeholder={t('org.stakeholder.territoryPh')}
        />
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}

      {showAdd && (
        <form onSubmit={add} style={sx.card}>
          <SelectField label={t('org.field.role')} name="roleType" value={roleType} onChange={(v) => setRoleType(v as StakeholderRoleType)} options={toOptions(STAKEHOLDER_ROLE_TYPES)} required />
          <SelectField label={t('org.field.employee')} name="employeeId" value={employeeId} onChange={setEmployeeId} options={employeeOptions} required />
          <CheckboxField label={t('org.stakeholder.primary')} name="isPrimary" checked={isPrimary} onChange={(v) => { setIsPrimary(v); if (v) setIsBackup(false); }} />
          <CheckboxField label={t('org.stakeholder.backup')} name="isBackup" checked={isBackup} onChange={(v) => { setIsBackup(v); if (v) setIsPrimary(false); }} />
          <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
          <div style={sx.actionBar}>
            <Button type="submit" variant="primary" disabled={busy}>{busy ? t('org.kit.saving') : t('org.stakeholder.save')}</Button>
            <Button type="button" variant="secondary" onClick={() => setShowAdd(false)}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}

      {!territoryId && <p>{t('org.stakeholder.selectToView')}</p>}
      {territoryId && loading && <p>{t('common.loading')}</p>}
      {territoryId && !loading && rows.length === 0 && <p>{t('org.stakeholder.empty')}</p>}
      {territoryId && rows.length > 0 && (
        <DataTable<StakeholderRow>
          rowKey={(s) => s.stakeholderId}
          rows={rows}
          columns={[
            { key: 'employee', header: t('org.col.employee'), render: (s) => empName[s.employeeId] ?? s.employeeId },
            { key: 'role', header: t('org.col.role'), render: (s) => s.roleType },
            { key: 'primary', header: t('org.col.primary'), render: (s) => s.isPrimary ? t('org.common.yes') : '—' },
            { key: 'backup', header: t('org.col.backup'), render: (s) => s.isBackup ? t('org.common.yes') : '—' },
            { key: 'from', header: t('org.col.from'), render: (s) => day(s.effectiveFromDate) },
            { key: 'to', header: t('org.col.to'), render: (s) => s.effectiveToDate && s.effectiveToDate !== FAR_FUTURE ? day(s.effectiveToDate) : t('org.common.active') },
            { key: 'actions', header: t('org.col.actions'), render: (s) => <Button type="button" variant="danger" onClick={() => remove(s.stakeholderId)}>{t('org.stakeholder.deactivate')}</Button> },
          ]}
        />
      )}
    </main>
  );
}
